import { useAuthStore } from '@/store/auth';

/**
 * Structured auth-event logging.
 *
 * Wraps useAuthStore.clearAuth() with a reason tag + context blob
 * so we can diagnose silent-logout bounces from the event trail
 * instead of guessing. Every event:
 *   - logs to console with `[auth]` prefix
 *   - appends to a ring buffer at localStorage['evi-auth-events']
 *     (max 50 entries, oldest dropped first)
 *
 * Inspect from DevTools:
 *   JSON.parse(localStorage.getItem('evi-auth-events'))
 *
 * The buffer survives a logout (different localStorage key than
 * `evi-web-auth`), so the founder can inspect the events that led
 * up to a bounce even after the bounce cleared in-memory state.
 *
 * Why structured: prior bounces were diagnosed via question-and-
 * defer cycles — "happened again, what triggered it?" with no data.
 * This module makes every clearAuth path leave a forensic
 * breadcrumb so the next bounce diagnoses itself.
 */

export type AuthLogoutReason =
  | 'manual-logout'
  | 'refresh-failed-401'
  | 'refresh-failed-403'
  | 'storage-event-cross-tab'
  | 'sub-mismatch'
  | 'boot-no-cookie'
  | 'boot-me-failed'
  | 'unknown';

/** Event kinds the ring buffer holds. These are observational and NOT a
 *  logout:
 *   - `boot-check`: cookie reachability probe on app mount.
 *   - `refresh-failed-transient`: a refresh failed twice without a 401 or
 *     403 (network error, timeout, 408, 429, 5xx); the session was kept.
 *   - `boot-refresh-failed`: the startup restore's refresh did not return
 *     a token (status and server message say why).
 *   - `boot-timeout`: the startup restore ran past its cap. */
type AuthEventKind =
  | AuthLogoutReason
  | 'boot-check'
  | 'refresh-failed-transient'
  | 'boot-refresh-failed'
  | 'boot-timeout';

interface AuthEvent {
  reason: AuthEventKind;
  route: string;
  timestamp: string;
  [key: string]: unknown;
}

const RING_BUFFER_KEY = 'evi-auth-events';
const MAX_EVENTS = 50;

function readBuffer(): AuthEvent[] {
  try {
    const raw = localStorage.getItem(RING_BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as AuthEvent[]) : [];
  } catch {
    return [];
  }
}

function writeBuffer(events: AuthEvent[]): void {
  try {
    // Slice from the END so the OLDEST entries drop first when the
    // buffer overflows — the recent tail is what diagnoses bounces.
    const trimmed = events.length > MAX_EVENTS
      ? events.slice(events.length - MAX_EVENTS)
      : events;
    localStorage.setItem(RING_BUFFER_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota / private-mode / SSR — observational logging, fail silently.
  }
}

/**
 * Append an auth event to the ring buffer and console. Public so
 * the boot-time cookie check (non-logout event) can use it without
 * going through logoutWithReason.
 */
export function appendAuthEvent(
  event: { reason: AuthEventKind } & Record<string, unknown>,
): void {
  const full: AuthEvent = {
    timestamp: new Date().toISOString(),
    route: typeof window !== 'undefined' ? window.location.pathname : '',
    ...event,
  };
  const buf = readBuffer();
  buf.push(full);
  writeBuffer(buf);
  // eslint-disable-next-line no-console
  console.log(`[auth] ${full.reason}`, full);
}

/**
 * Drop in-memory auth state + record a reason for the event log.
 * Replaces direct `useAuthStore.getState().clearAuth()` calls at
 * every logout site. The store's `clearAuth` action still exists
 * as the underlying state primitive; this wrapper is the call
 * surface for any "we're logging the user out" intent.
 */
export function logoutWithReason(
  reason: AuthLogoutReason,
  context?: Record<string, unknown>,
): void {
  appendAuthEvent({ reason, ...(context ?? {}) });
  useAuthStore.getState().clearAuth();
}

/** Read-only access to the event ring buffer. For diagnostic dumps. */
export function readAuthEvents(): AuthEvent[] {
  return readBuffer();
}
